import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { serviciosAPI } from '../services/api';

const CATEGORIAS = [
  { id: 'consulta',      label: 'Consultas',       icon: 'bi-person-hearts',   color: '#0984e3' },
  { id: 'examen',        label: 'Exámenes',        icon: 'bi-clipboard-pulse', color: '#6c5ce7' },
  { id: 'procedimiento', label: 'Procedimientos',  icon: 'bi-bandaid',         color: '#00b894' },
  { id: 'adaptacion_lc', label: 'Adaptación LC',   icon: 'bi-eye',             color: '#e17055' },
  { id: 'otro',          label: 'Otros',           icon: 'bi-three-dots',      color: '#636e72' }
];

const emptyForm = {
  codigo: '',
  nombre: '',
  descripcion: '',
  categoria: 'consulta',
  precio: '',
  duracion_minutos: '',
  requiere_optometrista: true,
  activo: true
};

const ServiciosCatalogo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [filtros, setFiltros] = useState({ activo: 'true', categoria: '', buscar: '' });
  const [categoriaActiva, setCategoriaActiva] = useState('');

  useEffect(() => { load(); }, [filtros.activo]);

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtros.activo !== '') params.activo = filtros.activo;
      const res = await serviciosAPI.getAll({ ...params, limit: 200 });
      if (res.data.success) setItems(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return items.filter(s => {
      if (categoriaActiva && s.categoria !== categoriaActiva) return false;
      if (filtros.buscar) {
        const q = filtros.buscar.toLowerCase();
        return (s.nombre?.toLowerCase().includes(q) || s.codigo?.toLowerCase().includes(q) || s.descripcion?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [items, categoriaActiva, filtros.buscar]);

  const stats = useMemo(() => ({
    total: items.length,
    activos: items.filter(s => s.activo).length,
    porCategoria: CATEGORIAS.map(c => ({ ...c, count: items.filter(s => s.categoria === c.id && s.activo).length })),
    precioPromedio: items.length ? (items.reduce((a, s) => a + parseFloat(s.precio || 0), 0) / items.length) : 0
  }), [items]);

  const openModal = (item = null) => {
    if (item) {
      setEditando(item);
      setFormData({
        codigo: item.codigo || '',
        nombre: item.nombre || '',
        descripcion: item.descripcion || '',
        categoria: item.categoria || 'otro',
        precio: item.precio || '',
        duracion_minutos: item.duracion_minutos || '',
        requiere_optometrista: item.requiere_optometrista ?? true,
        activo: item.activo ?? true
      });
    } else {
      setEditando(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre || formData.nombre.trim().length < 3) {
      toast.error('El nombre es requerido (mínimo 3 caracteres)');
      return;
    }
    if (formData.precio === '' || isNaN(parseFloat(formData.precio)) || parseFloat(formData.precio) < 0) {
      toast.error('El precio debe ser un número >= 0');
      return;
    }

    const payload = {
      ...formData,
      precio: parseFloat(formData.precio),
      duracion_minutos: formData.duracion_minutos ? parseInt(formData.duracion_minutos) : null
    };
    if (!payload.codigo) delete payload.codigo;

    try {
      if (editando) {
        await serviciosAPI.update(editando.id, payload);
        toast.success('Servicio actualizado');
      } else {
        await serviciosAPI.create(payload);
        toast.success('Servicio creado');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar');
    }
  };

  const handleToggleActivo = async (item) => {
    try {
      if (item.activo) {
        if (!window.confirm(`¿Desactivar el servicio "${item.nombre}"?`)) return;
        await serviciosAPI.remove(item.id);
        toast.success('Servicio desactivado');
      } else {
        await serviciosAPI.update(item.id, { activo: true });
        toast.success('Servicio reactivado');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error');
    }
  };

  const getCategoria = (id) => CATEGORIAS.find(c => c.id === id) || CATEGORIAS[4];

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h1 className="h3 mb-1 fw-bold">
            <i className="bi bi-heart-pulse me-2" style={{ color: '#0984e3' }}></i>
            Catálogo de Servicios
          </h1>
          <p className="text-muted mb-0 small">Gestiona los servicios profesionales de salud que factura la clínica</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <i className="bi bi-plus-lg me-2"></i>Nuevo servicio
        </button>
      </div>

      {/* Stats cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small">Total servicios</div>
                  <div className="h3 mb-0 fw-bold" style={{ color: '#0984e3' }}>{stats.total}</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: 'rgba(9,132,227,0.1)' }}>
                  <i className="bi bi-collection-fill fs-4" style={{ color: '#0984e3' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small">Activos</div>
                  <div className="h3 mb-0 fw-bold text-success">{stats.activos}</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: 'rgba(0,184,148,0.1)' }}>
                  <i className="bi bi-check-circle-fill fs-4 text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small">Precio promedio</div>
                  <div className="h3 mb-0 fw-bold" style={{ color: '#6c5ce7' }}>${stats.precioPromedio.toFixed(2)}</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: 'rgba(108,92,231,0.1)' }}>
                  <i className="bi bi-cash-coin fs-4" style={{ color: '#6c5ce7' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-muted small">Inactivos</div>
                  <div className="h3 mb-0 fw-bold text-secondary">{stats.total - stats.activos}</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: 'rgba(108,117,125,0.1)' }}>
                  <i className="bi bi-archive fs-4 text-secondary"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category chips */}
      <div className="mb-3 d-flex flex-wrap gap-2">
        <button
          className={`btn btn-sm ${categoriaActiva === '' ? 'btn-dark' : 'btn-outline-secondary'}`}
          onClick={() => setCategoriaActiva('')}
        >
          <i className="bi bi-grid me-1"></i>Todas ({stats.activos})
        </button>
        {CATEGORIAS.map(cat => (
          <button
            key={cat.id}
            className={`btn btn-sm ${categoriaActiva === cat.id ? 'btn-dark' : 'btn-outline-secondary'}`}
            onClick={() => setCategoriaActiva(cat.id)}
          >
            <i className={`${cat.icon} me-1`} style={{ color: categoriaActiva === cat.id ? undefined : cat.color }}></i>
            {cat.label} ({stats.porCategoria.find(c => c.id === cat.id)?.count || 0})
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input
                  className="form-control"
                  placeholder="Buscar por nombre, código o descripción..."
                  value={filtros.buscar}
                  onChange={e => setFiltros({ ...filtros, buscar: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={filtros.activo}
                onChange={e => setFiltros({ ...filtros, activo: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="true">Solo activos</option>
                <option value="false">Solo inactivos</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" onClick={() => { setFiltros({ activo: 'true', categoria: '', buscar: '' }); setCategoriaActiva(''); }}>
                <i className="bi bi-x-lg me-1"></i>Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Listado */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : filtered.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
            <h5>No hay servicios</h5>
            <p className="text-muted mb-3">{items.length === 0 ? 'Crea tu primer servicio para empezar' : 'No hay servicios que coincidan con los filtros'}</p>
            <button className="btn btn-primary" onClick={() => openModal()}><i className="bi bi-plus-lg me-2"></i>Nuevo servicio</button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {filtered.map(s => {
            const cat = getCategoria(s.categoria);
            return (
              <div key={s.id} className="col-sm-6 col-lg-4 col-xl-3">
                <div className="card h-100 border-0 shadow-sm" style={{ transition: 'transform .15s, box-shadow .15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, background: `${cat.color}15` }}>
                        <i className={cat.icon} style={{ fontSize: '1.25rem', color: cat.color }}></i>
                      </div>
                      <span className={`badge ${s.activo ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`}>
                        {s.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="mb-1 small text-muted fw-medium">{s.codigo}</div>
                    <h6 className="mb-1 fw-bold" style={{ minHeight: '2.5em' }}>{s.nombre}</h6>
                    {s.descripcion && <p className="small text-muted mb-2" style={{ fontSize: '.75rem', minHeight: '2em' }}>{s.descripcion.length > 60 ? s.descripcion.slice(0, 60) + '...' : s.descripcion}</p>}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="badge" style={{ background: `${cat.color}20`, color: cat.color, fontSize: '.7rem' }}>{cat.label}</span>
                      {s.duracion_minutos && <span className="small text-muted"><i className="bi bi-clock me-1"></i>{s.duracion_minutos}min</span>}
                    </div>
                    <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-2">
                      <div>
                        <div className="text-muted" style={{ fontSize: '.7rem' }}>Precio</div>
                        <div className="fw-bold" style={{ fontSize: '1.25rem', color: cat.color }}>${parseFloat(s.precio).toFixed(2)}</div>
                      </div>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-primary" onClick={() => openModal(s)} title="Editar">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className={`btn ${s.activo ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => handleToggleActivo(s)} title={s.activo ? 'Desactivar' : 'Reactivar'}>
                          <i className={`bi ${s.activo ? 'bi-slash-circle' : 'bi-check-circle'}`}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <form onSubmit={handleSubmit} className="modal-content border-0 shadow-lg">
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #6c5ce7 100%)', color: 'white' }}>
                <h5 className="modal-title fw-bold">
                  <i className={`bi ${editando ? 'bi-pencil-square' : 'bi-plus-circle-fill'} me-2`}></i>
                  {editando ? 'Editar servicio' : 'Nuevo servicio'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-medium">Código</label>
                    <input
                      className="form-control"
                      placeholder="Auto (SERV-####)"
                      value={formData.codigo}
                      onChange={e => setFormData({ ...formData, codigo: e.target.value })}
                      disabled={!!editando}
                    />
                    <small className="text-muted">Se autogenera si lo dejas vacío</small>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-medium">Nombre <span className="text-danger">*</span></label>
                    <input
                      className="form-control"
                      required
                      minLength={3}
                      value={formData.nombre}
                      onChange={e => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Ej: Consulta optométrica completa"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-medium">Descripción</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={formData.descripcion}
                      onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                      placeholder="Descripción breve del servicio (opcional)"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium">Categoría <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={formData.categoria}
                      onChange={e => setFormData({ ...formData, categoria: e.target.value })}
                    >
                      {CATEGORIAS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-medium">Precio USD <span className="text-danger">*</span></label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        required
                        min="0"
                        step="0.01"
                        value={formData.precio}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                        onChange={e => setFormData({ ...formData, precio: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-medium">Duración (min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.duracion_minutos}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                      onChange={e => setFormData({ ...formData, duracion_minutos: e.target.value })}
                      placeholder="30"
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="requiere-opt"
                        checked={formData.requiere_optometrista}
                        onChange={e => setFormData({ ...formData, requiere_optometrista: e.target.checked })}
                      />
                      <label htmlFor="requiere-opt" className="form-check-label">
                        Requiere optometrista (profesional de salud)
                      </label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="activo-sw"
                        checked={formData.activo}
                        onChange={e => setFormData({ ...formData, activo: e.target.checked })}
                      />
                      <label htmlFor="activo-sw" className="form-check-label">
                        Activo (aparece en el POS)
                      </label>
                    </div>
                  </div>
                </div>
                <div className="alert alert-info mt-3 mb-0" style={{ fontSize: '.8rem' }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Los servicios de salud están <strong>exentos de IVA</strong> según el Art. 56 LRTI. El precio se cobra tal cual.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">
                  <i className="bi bi-check-lg me-1"></i>{editando ? 'Guardar cambios' : 'Crear servicio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiciosCatalogo;
